<template>
  <div class="bg-gray-50 border border-gray-400 rounded-xs p-5" id="iod-results-filters">
    <SelectGroup
      group-label="Disability"
      placeholder="Select a disability"
      :filter-group="disabilityOptions"
      v-model="selectedDisability"
      @update:model-value="handleDisabilityChange"
      gbTheme="purple"
      :triggerLabelClasses="['truncate']"
    />

    <div class="mb-5 last:mb-0">
      <h2 class="text-base font-bold leading-5 m-0 mb-1 p-1 uppercase">Subjects</h2>
      <div class="flex flex-col gap-2">
        <div
          v-for="subject in subjectOptions"
          :key="subject.value"
          class="flex items-center gap-3 p-1 hover:bg-gray-100 hover:rounded"
        >
          <Checkbox
            :modelValue="selectedSubjects.includes(subject.value)"
            @update:modelValue="toggleSubject(subject.value)"
            :binary="true"
            :inputId="`subject-${subject.value}`"
            gbTheme="purple"
          />
          <label :for="`subject-${subject.value}`" class="text-base cursor-pointer">
            {{ subject.label }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import SelectGroup from '@/componentlibrary/SelectGroup.vue'
import Checkbox from 'primevue/checkbox'
import type { DisabilityOption, SubjectOption } from './impact-of-disability_types'

interface Props {
  disability: string
  disabilityOptions: DisabilityOption[]
  subjects: string[]
  subjectOptions: SubjectOption[]
  changeRequiresConfirmation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  changeRequiresConfirmation: false
})

const emit = defineEmits<{
  'update:disability': [value: string]
  'update:subjects': [value: string[]]
  'confirmupdate:disability': [value: string]
  'confirmupdate:subjects': [value: string]
}>()

const selectedDisability = ref(props.disability)
const selectedSubjects = ref<string[]>([...props.subjects])

const disabilityLabel = computed(() => {
  if (props.disability) {
    const option = props.disabilityOptions.find(elem => elem.value === props.disability)
    return option?.label || ''
  }
  return ''
})

watch(() => props.disability, (newVal) => {
  selectedDisability.value = newVal
})

watch(() => props.subjects, (newVal) => {
  selectedSubjects.value = [...newVal]
})

function handleDisabilityChange(newValue: string) {
  const option = props.disabilityOptions.find(opt => opt.value === newValue)
  if (option && !option.locked) {
    if (props.changeRequiresConfirmation) {
      emit('confirmupdate:disability', newValue)
    } else {
      selectedDisability.value = newValue
      emit('update:disability', newValue)
    }
  }
}

function toggleSubject(subjectValue: string) {
  if (props.changeRequiresConfirmation) {
    emit('confirmupdate:subjects', subjectValue)
  } else {
    if (selectedSubjects.value.includes(subjectValue)) {
      selectedSubjects.value = selectedSubjects.value.filter(s => s !== subjectValue)
    } else {
      selectedSubjects.value = [...selectedSubjects.value, subjectValue]
    }
    emit('update:subjects', selectedSubjects.value)
  }
}
</script>

